import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { User, Bell, Palette, Save } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    business_name: '',
    address: ''
  });

  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    sms_notifications: false,
    whatsapp_notifications: false,
    payment_reminders: true,
    due_date_alerts: true,
    maintenance_updates: true
  });

  const [appPreferences, setAppPreferences] = useState({
    theme: 'light',
    language: 'en',
    currency: 'INR',
    date_format: 'DD/MM/YYYY',
    timezone: 'Asia/Kolkata'
  });

  const fetchSettings = useCallback(async () => {
    try {
      const [profileRes, notifRes, prefRes] = await Promise.all([
        axios.get(`${API}/settings/profile`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings/notifications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings/preferences`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setProfileData({
        name: profileRes.data.name || '',
        email: profileRes.data.email || '',
        business_name: profileRes.data.business_name || '',
        address: profileRes.data.address || ''
      });
      setNotificationSettings(notifRes.data);
      setAppPreferences(prefRes.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateProfile = async () => {
    try {
      await axios.put(`${API}/settings/profile`, profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Profile Updated", description: "Your profile has been updated successfully" });
    } catch (error) {
      console.error('Profile update error:', error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to update profile";
      toast({ 
        title: "Error", 
        description: errorMessage, 
        variant: "destructive" 
      });
    }
  };

  const updateNotifications = async () => {
    try {
      await axios.put(`${API}/settings/notifications`, notificationSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Settings Updated", description: "Notification settings updated successfully" });
    } catch (error) {
      console.error('Notification settings update error:', error);
      
      let errorMessage = "Failed to update settings";
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => `${err.loc?.[err.loc.length - 1] || 'field'}: ${err.msg}`).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({ 
        title: "Error", 
        description: errorMessage, 
        variant: "destructive" 
      });
    }
  };

  const updatePreferences = async () => {
    try {
      await axios.put(`${API}/settings/preferences`, appPreferences, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Preferences Updated", description: "App preferences updated successfully" });
    } catch (error) {
      console.error('Preferences update error:', error);
      
      let errorMessage = "Failed to update preferences";
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => `${err.loc?.[err.loc.length - 1] || 'field'}: ${err.msg}`).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({ 
        title: "Error", 
        description: errorMessage, 
        variant: "destructive" 
      });
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your account preferences</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('profile')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'profile' 
              ? 'text-blue-600 border-b-2 border-blue-600' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <User className="inline mr-2" size={18} />
          Profile
        </button>
        <button
          onClick={() => setActiveTab('notifications')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'notifications' 
              ? 'text-blue-600 border-b-2 border-blue-600' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Bell className="inline mr-2" size={18} />
          Notifications
        </button>
        <button
          onClick={() => setActiveTab('preferences')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'preferences' 
              ? 'text-blue-600 border-b-2 border-blue-600' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Palette className="inline mr-2" size={18} />
          Preferences
        </button>
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <Card className="p-6">
          <h3 className="text-lg font-bold mb-4">Profile Information</h3>
          <div className="space-y-4 max-w-2xl">
            <div>
              <Label>Full Name</Label>
              <Input
                value={profileData.name}
                onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                placeholder="Enter your name"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                placeholder="your@email.com"
              />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input
                value={user?.phone || ''}
                disabled
                className="bg-gray-100"
              />
            </div>
            <div>
              <Label>Business Name</Label>
              <Input
                value={profileData.business_name}
                onChange={(e) => setProfileData({...profileData, business_name: e.target.value})}
                placeholder="Your business name"
              />
            </div>
            <div>
              <Label>Address</Label>
              <textarea
                value={profileData.address}
                onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Business address"
              />
            </div>
            <Button onClick={updateProfile} className="bg-blue-600 hover:bg-blue-700">
              <Save className="mr-2 h-4 w-4" />
              Save Profile
            </Button>
          </div>
        </Card>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <Card className="p-6">
          <h3 className="text-lg font-bold mb-4">Notification Preferences</h3>
          <div className="space-y-4 max-w-2xl">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">Email Notifications</p>
                <p className="text-sm text-gray-600">Receive notifications via email</p>
              </div>
              <input
                type="checkbox"
                checked={notificationSettings.email_notifications}
                onChange={(e) => setNotificationSettings({...notificationSettings, email_notifications: e.target.checked})}
                className="w-5 h-5"
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">SMS Notifications</p>
                <p className="text-sm text-gray-600">Receive notifications via SMS</p>
              </div>
              <input
                type="checkbox"
                checked={notificationSettings.sms_notifications}
                onChange={(e) => setNotificationSettings({...notificationSettings, sms_notifications: e.target.checked})}
                className="w-5 h-5"
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">WhatsApp Notifications</p>
                <p className="text-sm text-gray-600">Receive notifications on WhatsApp</p>
              </div>
              <input
                type="checkbox"
                checked={notificationSettings.whatsapp_notifications}
                onChange={(e) => setNotificationSettings({...notificationSettings, whatsapp_notifications: e.target.checked})}
                className="w-5 h-5"
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">Payment Reminders</p>
                <p className="text-sm text-gray-600">Send rent payment reminders to tenants</p>
              </div>
              <input
                type="checkbox"
                checked={notificationSettings.payment_reminders}
                onChange={(e) => setNotificationSettings({...notificationSettings, payment_reminders: e.target.checked})}
                className="w-5 h-5"
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">Due Date Alerts</p>
                <p className="text-sm text-gray-600">Get alerts for upcoming due dates</p>
              </div>
              <input
                type="checkbox"
                checked={notificationSettings.due_date_alerts}
                onChange={(e) => setNotificationSettings({...notificationSettings, due_date_alerts: e.target.checked})}
                className="w-5 h-5"
              />
            </div>
            <Button onClick={updateNotifications} className="bg-blue-600 hover:bg-blue-700">
              <Save className="mr-2 h-4 w-4" />
              Save Notification Settings
            </Button>
          </div>
        </Card>
      )}

      {/* Preferences Tab */}
      {activeTab === 'preferences' && (
        <Card className="p-6">
          <h3 className="text-lg font-bold mb-4">App Preferences</h3>
          <div className="space-y-4 max-w-2xl">
            <div>
              <Label>Theme</Label>
              <select
                value={appPreferences.theme}
                onChange={(e) => setAppPreferences({...appPreferences, theme: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="auto">Auto (System)</option>
              </select>
            </div>
            <div>
              <Label>Language</Label>
              <select
                value={appPreferences.language}
                onChange={(e) => setAppPreferences({...appPreferences, language: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="en">English</option>
                <option value="hi">Hindi</option>
              </select>
            </div>
            <div>
              <Label>Currency</Label>
              <select
                value={appPreferences.currency}
                onChange={(e) => setAppPreferences({...appPreferences, currency: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="INR">INR (₹)</option>
                <option value="USD">USD ($)</option>
              </select>
            </div>
            <div>
              <Label>Date Format</Label>
              <select
                value={appPreferences.date_format}
                onChange={(e) => setAppPreferences({...appPreferences, date_format: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>
            <div>
              <Label>Timezone</Label>
              <Input
                value={appPreferences.timezone}
                onChange={(e) => setAppPreferences({...appPreferences, timezone: e.target.value})}
                disabled
                className="bg-gray-100"
              />
            </div>
            <Button onClick={updatePreferences} className="bg-blue-600 hover:bg-blue-700">
              <Save className="mr-2 h-4 w-4" />
              Save Preferences
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Settings;